// Words for "Your day, at a glance" and "Make time": clock times in the owner's zone, "now" and
// "next" with relative times, and free time said plainly. Pure: callers pass the clock and zone.
import { durationWords, inWords, type AgendaItem, type DayMoment } from "@shared/agenda";

/** The one encouraging line on the rail. */
export const NEXT_RIGHT_THING = "You don’t have to do everything. Just the next right thing.";

const DAY_MS = 86_400_000;

function format(
  iso: string | number,
  timeZone: string,
  o: Intl.DateTimeFormatOptions,
  locale = "en-US",
): string {
  try {
    return new Intl.DateTimeFormat(locale, { ...o, timeZone }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat(locale, o).format(new Date(iso));
  }
}

/** "2:00 PM" in `timeZone`. */
export function clock(iso: string, timeZone: string): string {
  return format(iso, timeZone, { hour: "numeric", minute: "2-digit" });
}

/** "9:40–10:30 AM", "11:30 AM–12:15 PM". */
export function clockRange(start: string, end: string, timeZone: string): string {
  const a = clock(start, timeZone);
  const b = clock(end, timeZone);
  const suffix = (s: string) => s.slice(-2);
  return suffix(a) === suffix(b) ? `${a.slice(0, -3)}–${b}` : `${a}–${b}`;
}

/** Days since the epoch of the calendar day of `ms` in `timeZone`. */
function dayNumber(ms: number, timeZone: string): number {
  const [y, m, d] = format(
    ms,
    timeZone,
    { year: "numeric", month: "2-digit", day: "2-digit" },
    "en-CA",
  )
    .split("-")
    .map(Number);
  return Math.round(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) / DAY_MS);
}

/** "Today", "Tomorrow", or the weekday ("Wed") of `iso` in `timeZone`. */
export function dayLabel(iso: string, now: number, timeZone: string): string {
  const diff = dayNumber(Date.parse(iso), timeZone) - dayNumber(now, timeZone);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return format(iso, timeZone, { weekday: "short" });
}

/** A suggested focus time: "Today · 2:00–2:30 PM", and when it starts ("in 25 min"). */
export function slotWords(
  slot: { start: string; end: string },
  now: number,
  timeZone: string,
): { label: string; relative: string } {
  return {
    label: `${dayLabel(slot.start, now, timeZone)} · ${clockRange(slot.start, slot.end, timeZone)}`,
    relative: inWords(now, Date.parse(slot.start)),
  };
}

export type RowState = "now" | "next" | "earlier" | null;

export interface RowWords {
  /** The whole time: "All day", "Today", "4:00 PM", "9:40–10:30 AM". */
  when: string;
  /** The time column of a compact row: when it starts ("9:40 AM", "All day", "Today"). */
  at: string;
  /** When a stretch ends ("until 10:30 AM"); null for tasks and all-day events. */
  until: string | null;
  state: RowState;
  /** "ends in 23 min", "in 1 h 5 min" (only for now and next). */
  detail: string | null;
}

/** How one rail row reads at `now`. */
export function rowWords(
  item: AgendaItem,
  moment: DayMoment,
  now: number,
  timeZone: string,
): RowWords {
  if (!item.start) {
    const label = item.kind === "task" ? "Today" : "All day";
    return { when: label, at: label, until: null, state: null, detail: null };
  }
  const times = {
    when: item.end ? clockRange(item.start, item.end, timeZone) : clock(item.start, timeZone),
    at: clock(item.start, timeZone),
    until: item.end ? `until ${clock(item.end, timeZone)}` : null,
  };
  if (moment.now.includes(item) && item.end) {
    const left = Math.round((Date.parse(item.end) - now) / 60_000);
    const detail = left >= 1 ? `ends in ${durationWords(left)}` : "ending now";
    return { ...times, state: "now", detail };
  }
  if (moment.next === item)
    return { ...times, state: "next", detail: inWords(now, Date.parse(item.start)) };
  if (moment.earlier.includes(item)) return { ...times, state: "earlier", detail: null };
  return { ...times, state: null, detail: null };
}

/** Free time, said as free: "Free until 1:30 PM", "Free for the rest of today". */
export function freeWords(free: DayMoment["free"], timeZone: string): string | null {
  if (!free) return null;
  return free.until ? `Free until ${clock(free.until, timeZone)}` : "Free for the rest of today";
}
